<script>
    import { createEventDispatcher } from "svelte";
    import {
        visibleMasterColumns,
        MASTER_COLUMNS_DEF,
        METRIC_DEFINITIONS,
        ALL_METRIC_KEYS,
        visibleMetricKeys,
        DEFAULT_VISIBLE_COLUMNS,
        ALL_WEEKS,
        autoWeekRange,
        defaultWeekRange,
        setDefaultWeekRange,
        saveSupplyPlanSettings,
        supplyPlanSearch,
        appliedSupplyPlanSearch,
    } from "./store.js";
    import { get } from "svelte/store";
    import { getDateRangeFromWeeks } from "./dateWeekUtil.js";
    import { fade, scale } from "svelte/transition";
    import toast from "svelte-french-toast";

    export let isOpen = false;
    const dispatch = createEventDispatcher();

    // 임시 편집용 컬럼 순서 배열
    let tempSelected = [];
    let selectedColKey = null;
    let prevOpen = false;

    // 임시 편집용 표시 항목(계획주차 오른쪽 6가지) 및 기본 조회구간 (현재 연도 기준 주차 지정)
    let currentYear = new Date().getFullYear();
    let tempMetrics = [];
    let tempWeekAuto = true;
    let tempStartWeek = 1;
    let tempEndWeek = 53;
    let isSaving = false;

    // 직접 지정 시 계산된 현재 연도 기준 날짜 정보
    $: tempDateRange = getDateRangeFromWeeks(currentYear, tempStartWeek, tempEndWeek);

    // 모달이 열릴 때만 현재 store 값으로 초기화 (열려있는 동안 부모 리렌더링으로 인한 덮어쓰기 방지)
    $: if (isOpen && !prevOpen) {
        tempSelected = [...$visibleMasterColumns];
        selectedColKey = tempSelected[0] || null;
        tempMetrics = [...$visibleMetricKeys];
        tempWeekAuto = !$defaultWeekRange.isCustom;
        tempStartWeek = $defaultWeekRange.startWeek || 1;
        tempEndWeek = $defaultWeekRange.endWeek || 53;
        currentYear = new Date().getFullYear();
        prevOpen = true;
    } else if (!isOpen) {
        prevOpen = false;
    }

    function handleClose() {
        dispatch("close");
    }

    function toggleColumn(key) {
        if (tempSelected.includes(key)) {
            if (tempSelected.length <= 1) {
                toast.error("최소 1개 이상의 컬럼이 표시되어야 합니다.");
                return;
            }
            tempSelected = tempSelected.filter(k => k !== key);
            if (selectedColKey === key) {
                selectedColKey = tempSelected[0] || null;
            }
        } else {
            tempSelected = [...tempSelected, key];
            selectedColKey = key;
        }
    }

    function toggleMetric(key) {
        if (tempMetrics.includes(key)) {
            if (tempMetrics.length <= 1) {
                toast.error("최소 1개 이상의 항목이 표시되어야 합니다.");
                return;
            }
            tempMetrics = tempMetrics.filter(k => k !== key);
        } else {
            // 항목 정의 순서(수주/판매/차질/출하/생산/재고)를 유지하며 추가
            tempMetrics = ALL_METRIC_KEYS.filter(k => k === key || tempMetrics.includes(k));
        }
    }

    function showAllMetrics() {
        tempMetrics = [...ALL_METRIC_KEYS];
    }

    function moveLeft() {
        if (!selectedColKey) return;
        const idx = tempSelected.indexOf(selectedColKey);
        if (idx > 0) {
            const next = [...tempSelected];
            const temp = next[idx - 1];
            next[idx - 1] = next[idx];
            next[idx] = temp;
            tempSelected = next;
        }
    }

    function moveRight() {
        if (!selectedColKey) return;
        const idx = tempSelected.indexOf(selectedColKey);
        if (idx !== -1 && idx < tempSelected.length - 1) {
            const next = [...tempSelected];
            const temp = next[idx + 1];
            next[idx + 1] = next[idx];
            next[idx] = temp;
            tempSelected = next;
        }
    }

    async function handleApply() {
        const startW = parseInt(tempStartWeek, 10);
        const endW = parseInt(tempEndWeek, 10);
        if (!tempWeekAuto && startW > endW) {
            toast.error("기본 조회구간의 시작 주차는 종료 주차보다 클 수 없습니다.");
            return;
        }

        const weekRange = tempWeekAuto ? null : { startWeek: startW, endWeek: endW };

        visibleMasterColumns.set([...tempSelected]);
        visibleMetricKeys.set([...tempMetrics]);
        setDefaultWeekRange(weekRange);

        // 변경된 기본 조회구간을 현재 검색 조건에도 즉시 반영
        const { startDate, endDate, startYear, endYear, startWeek, endWeek } = get(defaultWeekRange);
        const applyRange = s => s.monthSum ? s : { ...s, startDate, endDate, startYear, endYear, startWeek, endWeek };
        supplyPlanSearch.update(applyRange);
        appliedSupplyPlanSearch.update(applyRange);

        // 기기 로컬 저장소에 저장 (로그아웃 후에도 유지)
        isSaving = true;
        const saved = await saveSupplyPlanSettings({
            columns: [...tempSelected],
            metrics: [...tempMetrics],
            weekRange,
        });
        isSaving = false;

        if (saved) {
            toast.success("컬럼 설정이 저장되고 테이블에 적용되었습니다.", { duration: 1800 });
        } else {
            toast.error("설정 저장에 실패했습니다. (이번 세션에만 적용됩니다)");
        }
        dispatch("close");
    }

    function handleDefault() {
        tempSelected = [...DEFAULT_VISIBLE_COLUMNS];
        selectedColKey = DEFAULT_VISIBLE_COLUMNS[0];
    }

    // 제품 컬럼만 표시 (영업, 항목 제거)
    const productCols = MASTER_COLUMNS_DEF.filter(c => c.group === "제품" || !c.group);
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose} transition:fade={{ duration: 180 }}>
        <div class="modal-box" on:click|stopPropagation transition:scale={{ duration: 180, start: 0.95 }}>
            <!-- 모달 헤더 -->
            <div class="modal-header">
                <div class="modal-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <line x1="4" y1="21" x2="4" y2="14"/>
                        <line x1="4" y1="10" x2="4" y2="3"/>
                        <line x1="12" y1="21" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12" y2="3"/>
                        <line x1="20" y1="21" x2="20" y2="16"/>
                        <line x1="20" y1="12" x2="20" y2="3"/>
                        <line x1="1" y1="14" x2="7" y2="14"/>
                        <line x1="9" y1="8" x2="15" y2="8"/>
                        <line x1="17" y1="16" x2="23" y2="16"/>
                    </svg>
                    <h3>컬럼 설정 (제품 컬럼 / 표시 항목 / 기본 조회구간)</h3>
                </div>
                <button class="close-btn" on:click={handleClose} title="닫기">✕</button>
            </div>

            <!-- 모달 본문: 제품 컬럼 단독 구성 -->
            <div class="modal-body">
              <div class="modal-columns">
               <div class="modal-col modal-col-left">
                <div class="product-columns-panel">
                    <div class="section-title">
                        <span class="tree-arrow">▼</span>
                        <span>표시할 제품 컬럼 선택</span>
                        <span class="section-sub">(체크하면 즉시 아래 순서 바에 추가됩니다)</span>
                    </div>

                    <div class="product-grid">
                        {#each productCols as col}
                            {@const isChecked = tempSelected.includes(col.key)}
                            <div
                                class="check-item"
                                class:checked={isChecked}
                                on:click={() => toggleColumn(col.key)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    on:click|stopPropagation={() => toggleColumn(col.key)}
                                />
                                <div class="col-info">
                                    <span class="col-name">{col.label}</span>
                                    <span class="col-desc">{col.desc}</span>
                                </div>
                                {#if isChecked}
                                    <span class="col-badge">선택됨</span>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>

                <!-- 하단 선택된 컬럼 순서 태그 바 & 순서 이동 버튼 -->
                <div class="reorder-bar-container">
                    <div class="reorder-header">
                        <span class="reorder-title">선택된 컬럼 표시 순서 (클릭 후 ◀ ▶ 버튼으로 순서 이동)</span>
                        <button type="button" class="reset-order-btn" on:click={handleDefault}>기본값으로 복원</button>
                    </div>

                    <div class="reorder-wrapper">
                        <div class="chips-list">
                            {#each tempSelected as key, idx}
                                {@const def = MASTER_COLUMNS_DEF.find(c => c.key === key)}
                                {#if def}
                                    <button
                                        type="button"
                                        class="order-chip"
                                        class:selected={selectedColKey === key}
                                        on:click={() => selectedColKey = key}
                                    >
                                        <span class="chip-num">{idx + 1}</span>
                                        <span class="chip-text">{def.label}</span>
                                    </button>
                                {/if}
                            {/each}
                        </div>

                        <div class="arrow-buttons">
                            <button
                                type="button"
                                class="nav-arrow-btn"
                                on:click={moveLeft}
                                title="선택한 컬럼 왼쪽으로 이동"
                                disabled={!selectedColKey || tempSelected.indexOf(selectedColKey) <= 0}
                            >◀</button>
                            <button
                                type="button"
                                class="nav-arrow-btn"
                                on:click={moveRight}
                                title="선택한 컬럼 오른쪽으로 이동"
                                disabled={!selectedColKey || tempSelected.indexOf(selectedColKey) >= tempSelected.length - 1}
                            >▶</button>
                        </div>
                    </div>
                </div>
               </div>

               <div class="modal-col modal-col-right">
                <!-- 계획주차 오른쪽 항목(6가지) 표시 여부 -->
                <div class="product-columns-panel">
                    <div class="section-title">
                        <span class="tree-arrow">▼</span>
                        <span>계획주차 오른쪽 항목 표시</span>
                        <span class="section-sub">(체크 해제한 항목은 표에서 숨겨집니다)</span>
                        {#if tempMetrics.length < ALL_METRIC_KEYS.length}
                            <button type="button" class="reset-order-btn section-action" on:click={showAllMetrics}>전체 표시</button>
                        {/if}
                    </div>

                    <div class="product-grid">
                        {#each METRIC_DEFINITIONS as metric}
                            {@const isMetricChecked = tempMetrics.includes(metric.key)}
                            <div
                                class="check-item"
                                class:checked={isMetricChecked}
                                on:click={() => toggleMetric(metric.key)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isMetricChecked}
                                    on:click|stopPropagation={() => toggleMetric(metric.key)}
                                />
                                <div class="col-info">
                                    <span class="col-name">{metric.label}</span>
                                    <span class="col-desc" title={metric.summary}>{metric.summary}</span>
                                </div>
                                {#if isMetricChecked}
                                    <span class="col-badge">표시</span>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>

                <!-- 조회구간(주차) 기본값 -->
                <div class="product-columns-panel">
                    <div class="section-title">
                        <span class="tree-arrow">▼</span>
                        <span>조회구간(주차) 기본값</span>
                        <span class="section-sub">(페이지를 새로 열거나 초기화할 때 적용됩니다)</span>
                    </div>

                    <div class="product-grid">
                        <label
                            class="check-item"
                            class:checked={tempWeekAuto}
                        >
                            <input
                                type="radio"
                                name="weekDefaultMode"
                                bind:group={tempWeekAuto}
                                value={true}
                            />
                            <div class="col-info">
                                <span class="col-name">현재 주차 기준 자동</span>
                                <span class="col-desc">과거 1주 ~ 미래 19주 ({autoWeekRange.totalWeeks}주 구간)</span>
                            </div>
                        </label>
                        <label
                            class="check-item"
                            class:checked={!tempWeekAuto}
                        >
                            <input
                                type="radio"
                                name="weekDefaultMode"
                                bind:group={tempWeekAuto}
                                value={false}
                            />
                            <div class="col-info">
                                <span class="col-name">직접 지정 (주차)</span>
                                <span class="col-desc">주차를 지정하며 매년 해당 연도 일자로 자동 계산됩니다</span>
                            </div>
                        </label>
                    </div>

                    <div class="week-select-row" class:is-disabled={tempWeekAuto}>
                        <div class="modal-select-wrap">
                            <select class="modal-week-select" bind:value={tempStartWeek} disabled={tempWeekAuto}>
                                {#each ALL_WEEKS as w}
                                    <option value={w.week}>{w.label} ({w.month}월)</option>
                                {/each}
                            </select>
                        </div>
                        <span class="week-sep">~</span>
                        <div class="modal-select-wrap">
                            <select class="modal-week-select" bind:value={tempEndWeek} disabled={tempWeekAuto}>
                                {#each ALL_WEEKS as w}
                                    <option value={w.week}>{w.label} ({w.month}월)</option>
                                {/each}
                            </select>
                        </div>
                        {#if !tempWeekAuto && tempDateRange}
                            <span class="modal-range-badge" title="{currentYear}년 기준 적용 일자">
                                📅 {tempDateRange.startDate} ~ {tempDateRange.endDate}
                            </span>
                        {/if}
                    </div>
                </div>
               </div>
              </div>
            </div>

            <!-- 모달 푸터 -->
            <div class="modal-footer">
                <button class="btn btn-secondary" on:click={handleClose}>닫기</button>
                <button class="btn btn-primary" on:click={handleApply} disabled={isSaving}>저장 및 적용</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.65);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 16px;
    }
    .modal-box {
        background: white;
        width: 100%;
        max-width: 1080px;
        border-radius: 8px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.25);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #cbd5e1;
    }
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 18px;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
    }
    .modal-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #1e293b;
    }
    .modal-title h3 {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 800;
    }
    .close-btn {
        background: none;
        border: none;
        font-size: 1.15rem;
        color: #94a3b8;
        cursor: pointer;
        padding: 2px 6px;
        line-height: 1;
        transition: color 0.15s;
    }
    .close-btn:hover {
        color: #0f172a;
    }
    .modal-body {
        padding: 16px;
        max-height: 80vh;
        overflow-y: auto;
        overflow-x: hidden;
    }
    .modal-columns {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
        gap: 16px;
        align-items: start;
    }
    .modal-col {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 0;
    }
    @media (max-width: 900px) {
        .modal-columns {
            grid-template-columns: minmax(0, 1fr);
        }
    }

    .product-columns-panel {
        background: #f8fafc;
        padding: 14px;
        border-radius: 6px;
        border: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    .section-title {
        font-size: 0.82rem;
        font-weight: 800;
        color: #334155;
        display: flex;
        align-items: center;
        gap: 6px;
        border-bottom: 1.5px solid #cbd5e1;
        padding-bottom: 6px;
    }
    .section-sub {
        font-size: 0.72rem;
        font-weight: 500;
        color: #64748b;
        margin-left: 2px;
    }
    .tree-arrow {
        font-size: 0.65rem;
        color: #3b82f6;
    }

    .product-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }
    .check-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 12px;
        background: #ffffff;
        border: 1.5px solid #e2e8f0;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;
        user-select: none;
    }
    .check-item:hover {
        border-color: #93c5fd;
        background: #f0f7ff;
    }
    .check-item.checked {
        border-color: #3b82f6;
        background: #eff6ff;
        box-shadow: 0 1px 3px rgba(59, 130, 246, 0.12);
    }
    .check-item input[type="checkbox"],
    .check-item input[type="radio"] {
        width: 16px;
        height: 16px;
        accent-color: #3b82f6;
        cursor: pointer;
        flex-shrink: 0;
    }
    .col-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
    }
    .col-name {
        font-size: 0.85rem;
        font-weight: 700;
        color: #1e293b;
    }
    .check-item.checked .col-name {
        color: #1d4ed8;
    }
    .col-desc {
        font-size: 0.7rem;
        color: #64748b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .col-badge {
        font-size: 0.65rem;
        font-weight: 700;
        color: #2563eb;
        background: #dbeafe;
        padding: 2px 6px;
        border-radius: 10px;
        white-space: nowrap;
    }

    /* 하단 순서 변경 바 */
    .reorder-bar-container {
        border-top: 1px solid #e2e8f0;
        padding-top: 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .reorder-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .reorder-title {
        font-size: 0.78rem;
        font-weight: 800;
        color: #475569;
    }
    .reset-order-btn {
        background: none;
        border: none;
        color: #3b82f6;
        font-size: 0.75rem;
        font-weight: 700;
        cursor: pointer;
        text-decoration: underline;
        padding: 0;
    }
    .reset-order-btn:hover {
        color: #1d4ed8;
    }
    .reorder-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .chips-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        flex: 1;
        background: #f8fafc;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid #cbd5e1;
        min-height: 44px;
        align-items: center;
    }
    .order-chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: white;
        border: 1.5px solid #cbd5e1;
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: 700;
        color: #334155;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .order-chip:hover {
        border-color: #3b82f6;
        color: #1d4ed8;
    }
    .order-chip.selected {
        background: #eff6ff;
        border-color: #3b82f6;
        color: #1d4ed8;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
    }
    .chip-num {
        font-size: 0.68rem;
        background: #e2e8f0;
        color: #475569;
        padding: 1px 5px;
        border-radius: 3px;
        font-weight: 800;
    }
    .order-chip.selected .chip-num {
        background: #3b82f6;
        color: white;
    }

    .arrow-buttons {
        display: flex;
        gap: 4px;
    }
    .nav-arrow-btn {
        width: 36px;
        height: 36px;
        background: #ffffff;
        border: 1.5px solid #cbd5e1;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 800;
        color: #334155;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
    }
    .nav-arrow-btn:not(:disabled):hover {
        background: #3b82f6;
        border-color: #3b82f6;
        color: white;
    }
    .nav-arrow-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        background: #f1f5f9;
        border-color: #e2e8f0;
    }

    .modal-footer {
        padding: 10px 18px;
        border-top: 1px solid #e2e8f0;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        background: #f8fafc;
    }
    .btn {
        padding: 7px 18px;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .btn-secondary {
        background: white;
        border: 1px solid #cbd5e1;
        color: #475569;
    }
    .btn-secondary:hover {
        background: #f1f5f9;
    }
    .btn-primary {
        background: #3b82f6;
        border: 1px solid #3b82f6;
        color: white;
    }
    .btn-primary:hover {
        background: #2563eb;
    }
    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* 섹션 제목 우측 보조 버튼 (전체 표시 등) */
    .section-action {
        margin-left: auto;
    }

    /* 조회구간(주차) 기본값 선택 행 */
    .week-select-row {
        display: flex;
        align-items: center;
        gap: 8px;
        transition: opacity 0.15s ease;
    }
    .week-select-row.is-disabled {
        opacity: 0.55;
    }
    .modal-select-wrap {
        flex: 1;
        min-width: 0;
    }
    .modal-week-select {
        width: 100%;
        height: 35px;
        border: 1.5px solid #cbd5e1;
        border-radius: 4px;
        padding: 0 10px;
        font-size: 0.85rem;
        font-family: inherit;
        font-weight: 600;
        background-color: #fff;
        color: #1e293b;
        box-sizing: border-box;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .modal-week-select:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
    .modal-week-select:disabled {
        background-color: #f8fafc;
        color: #94a3b8;
        cursor: not-allowed;
    }
    .modal-range-badge {
        font-size: 0.74rem;
        font-weight: 700;
        color: #1e40af;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 6px;
        padding: 6px 10px;
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
    .week-sep {
        font-size: 0.85rem;
        color: #64748b;
        font-weight: 700;
    }
</style>
